import React, { ReactNode } from 'react';
import { stylesheet } from 'astroturf/react';
import pluralize from 'pluralize';

import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import routes from 'sitewide-js/core_pre_webpack/routes';
import PremiumIcon from 'js/premium/PremiumIcon';
import commaize from 'js/utils/commaize';
import { TAB } from './react/Tabs';

type Props = {
  // Allow external styling
  className?: string;
  // Number of search results
  numResults: number;

  // The search query
  query: string;

  // Currently selected search filters, undefined if set to default value
  publicationType?: 'journal-article' | 'conference-paper' | 'book' | 'other';
  languages?: Array<string>;
  pubAfterDate?: number;
  pubBeforeDate?: number;

  // Actions
  onClear: () => void;
  // for the search_full_text_upsell test
  tab?: TAB;
};

const SearchFiltersUpsell = ({
  className,
  numResults,
  query,
  publicationType,
  languages,
  pubAfterDate,
  pubBeforeDate,
  onClear,
  tab = TAB.Titles,
}: Props) => {
  const blue = (x: ReactNode) => <span className={styles.blue}>{x}</span>;

  const isFulltext = tab === TAB.FullText;

  const resultsPhrase = publicationType
    ? {
        'journal-article': 'journal articles',
        'conference-paper': 'conference papers',
        book: 'books',
        other: 'other results',
      }[publicationType]
    : 'result';

  const publicationTypePhrase = blue(
    `${commaize(numResults)} ${pluralize(resultsPhrase, numResults)}`,
  );

  let languagesPhrase;
  if (languages) {
    languagesPhrase = <span>in {toSentence(languages.map(blue))}</span>;
  }

  let title;
  let queryPhrase;
  let source;
  switch (tab) {
    case TAB.FullText:
      title = 'Full Text search found';
      queryPhrase = <span> with {blue(query)} in the full text </span>;
      source = 'search_filters_full_text_upsell';
      break;
    default:
      title = 'Title search refined to';
      queryPhrase = <span> matching {blue(query)} </span>;
      source = 'search_filters_upsell';
  }

  let publicationDatePhrase;
  if (pubAfterDate && pubBeforeDate) {
    publicationDatePhrase = (
      <span>between {blue(`${pubAfterDate} – ${pubBeforeDate}`)}</span>
    );
  } else if (pubAfterDate) {
    publicationDatePhrase = <span>since {blue(pubAfterDate)}</span>;
  } else if (pubBeforeDate) {
    publicationDatePhrase = <span>before {blue(pubBeforeDate)}</span>;
  }

  const someFilterSet = Boolean(
    pubAfterDate || pubBeforeDate || languages || publicationType,
  );

  return (
    <div className={className}>
      <ImpressionWatcher
        source={source}
        extra={{
          publicationType,
          languages,
          pubAfterDate,
          pubBeforeDate,
        }}
      >
        <div className={styles.upsell}>
          <div className={styles.title}>{title}</div>
          <div className={styles.text}>
            {publicationTypePhrase} {languagesPhrase}
            {queryPhrase}
            {publicationDatePhrase}
          </div>
          <button
            className={styles.cta}
            onClick={() => goToCheckout(query, tab)}
          >
            <PremiumIcon color="secondary" size={24} marginRight={14} />
            {`Upgrade to view ${commaize(numResults)} ${pluralize(
              'result',
              numResults,
            )}`}
          </button>
          {(!isFulltext || someFilterSet) && (
            <div className={styles.secondary}>
              or
              <span className={styles.clear} onClick={() => onClear()}>
                clear search filters
              </span>
            </div>
          )}
        </div>
      </ImpressionWatcher>
    </div>
  );
};

const goToCheckout = (query: string, tab: TAB) => {
  let trigger;
  let feature;
  switch (tab) {
    case TAB.FullText:
      trigger = 'search-filters-full-text-upsell';
      feature = 'ADVANCED_SEARCH';
      break;
    default:
      trigger = 'search-filters-upsell';
      feature = 'ADVANCED_SEARCH';
  }
  window.location.href = routes.checkout_url({
    feature: feature,
    trigger: trigger,
    upgradeRedirect: encodeURIComponent(routes.search_url({ q: query })),
  });
};

// Intersperse an array with commas and "and" to make a sentence.
// Basically $h.to_sentence but for React.
const toSentence = (array: Array<ReactNode>): Array<ReactNode> => {
  if (array.length < 2) {
    return array;
  } else if (array.length === 2) {
    return [array[0], ' and ', array[1]];
  } else {
    const separators = [''].concat(
      new Array(array.length - 2).fill(', '),
      ', and ',
    );
    return separators.reduce<ReactNode[]>(
      (arr, val, i) => arr.concat(val, array[i]),
      [],
    );
  }
};

const styles = stylesheet`
  .upsell {
    font-family: Roboto, sans-serif;
    color: #666;
    -webkit-font-smoothing: antialiased;

    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 50px 0;
    padding: 0 32px;

    @media (max-width: 767px) {
      margin: 32px 0;
      padding: 0 22px;
    }
  }

  .title {
    font-size: 24px;
    line-height: 28px;
    letter-spacing: -0.2px;
    margin-bottom: 6px;

    @media (max-width: 767px) {
      font-size: 18px;
      line-height: 21px;
    }
  }

  .text {
    font-weight: bold;
    font-size: 32px;
    line-height: 37px;
    text-align: center;
    letter-spacing: -0.2px;
    margin-bottom: 26px;

    @media (max-width: 767px) {
      font-size: 24px;
      line-height: 30px;
    }
  }

  .blue {
    color: #094ec3;
  }

  .cta {
    font-weight: bold;
    font-size: 19px;
    line-height: 22px;
    letter-spacing: 0.5px;
    color: white;
    padding: 15px 20px;
    margin-bottom: 13px;
    background: #094ec3;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  .secondary {
    font-size: 16px;
    line-height: 19px;
    letter-spacing: -0.2px;
    flex-wrap: no-wrap;
    display: flex;
  }

  .clear {
    margin-left: 12px;
    color: #094ec3;
    cursor: pointer;
  }
`;

export default SearchFiltersUpsell;
